import { useCallback, useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'
import {
  clearPaymentPending,
  isPaymentPending,
  notifyCreditsChanged,
  subscribeToCreditsChanged,
  PAYMENT_POLL_INTERVAL_MS,
} from '../utils/creditsSync'

export interface ScanCreditsState {
  isPro: boolean
  scansCount: number
  /** True only until the very first fetch resolves. */
  loading: boolean
  /** True while a background/manual refresh is in flight. */
  refreshing: boolean
  /** True once the backend row has been read successfully at least once. */
  hasLoaded: boolean
  canScan: boolean
  refetchCredits: () => Promise<void>
  /** Manual refresh (e.g. a "Refresh credits" button). Never throws. */
  refresh: () => Promise<void>
  verifyCanScan: () => Promise<{ allowed: boolean; reason?: string }>
  /** Hard error: credits have never loaded, so entitlement is unknown. */
  refetchError: string | null
  /** Soft error: a refresh failed but last-known-good values are still shown. */
  refreshError: string | null
}

const MAX_INITIAL_ATTEMPTS = 3
const RETRY_DELAYS_MS = [400, 1200]

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

export function useScanCredits(): ScanCreditsState {
  const { user } = useAuth()
  const [isPro, setIsPro] = useState(false)
  const [scansCount, setScansCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [refetchError, setRefetchError] = useState<string | null>(null)
  const [refreshError, setRefreshError] = useState<string | null>(null)

  // Kept in refs so listeners/intervals always read current values without
  // re-subscribing on every credit change.
  const hasLoadedRef = useRef(false)
  const isProRef = useRef(false)
  const scansCountRef = useRef(0)
  const inFlightRef = useRef(false)

  const applyProfile = useCallback((next: { isPro: boolean; scansCount: number }) => {
    isProRef.current = next.isPro
    scansCountRef.current = next.scansCount
    hasLoadedRef.current = true
    setIsPro(next.isPro)
    setScansCount(next.scansCount)
    setHasLoaded(true)
    setRefetchError(null)
    setRefreshError(null)
    // Payment has landed in the DB — stop the post-payment polling window.
    if (next.isPro || next.scansCount > 0) {
      clearPaymentPending()
    }
  }, [])

  const fetchProfile = useCallback(async (): Promise<{
    isPro: boolean
    scansCount: number
  } | null> => {
    if (!user?.id) return null

    const { data, error } = await supabase
      .from('profiles')
      .select('is_pro, scan_credits')
      .eq('id', user.id)
      .single()

    if (error) throw error

    return {
      isPro: Boolean(data?.is_pro),
      scansCount: Number(data?.scan_credits ?? 0),
    }
  }, [user?.id])

  /**
   * Read the row and replace local state with backend truth.
   * `silent` keeps the UI from flashing a spinner for background polls.
   */
  const load = useCallback(
    async (options?: { silent?: boolean; attempts?: number }) => {
      if (!user?.id || inFlightRef.current) return
      const attempts = options?.attempts ?? 1
      inFlightRef.current = true
      if (!options?.silent) setRefreshing(true)

      let lastError: unknown = null
      for (let attempt = 0; attempt < attempts; attempt++) {
        try {
          const result = await fetchProfile()
          if (result) {
            applyProfile(result)
            lastError = null
            break
          }
        } catch (err) {
          lastError = err
          if (attempt < attempts - 1) {
            await wait(RETRY_DELAYS_MS[attempt] ?? 1200)
          }
        }
      }

      if (lastError) {
        console.error('[useScanCredits] fetch failed:', lastError)
        if (hasLoadedRef.current) {
          // Non-blocking: keep last-known-good credits on screen.
          setRefreshError('Could not refresh credits')
        } else {
          setRefetchError('Failed to load credits')
        }
      }

      inFlightRef.current = false
      setRefreshing(false)
      setLoading(false)
    },
    [user?.id, fetchProfile, applyProfile],
  )

  // Initial fetch on mount + realtime, focus, event and post-payment polling.
  useEffect(() => {
    if (!user?.id) {
      setLoading(false)
      setHasLoaded(false)
      hasLoadedRef.current = false
      return
    }

    let cancelled = false

    // 1. Mount fetch, with a couple of quick retries so a cold start or a
    //    momentary network blip doesn't strand the UI on "FREE".
    void load({
      silent: true,
      attempts: MAX_INITIAL_ATTEMPTS,
    })

    // 2. Realtime: the webhook writing `is_pro` / `scan_credits` pushes here.
    const channel = supabase
      .channel(`profile-credits-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${user.id}`,
        },
        (payload) => {
          if (cancelled) return
          const next = payload.new as {
            is_pro?: boolean
            scan_credits?: number
          }
          applyProfile({
            isPro: Boolean(next?.is_pro),
            scansCount: Number(next?.scan_credits ?? 0),
          })
          setLoading(false)
        },
      )
      .subscribe()

    // 3. Any other screen (payment callback, scan completion) can poke us.
    const unsubscribe = subscribeToCreditsChanged(() => {
      if (!cancelled) void load({ silent: true })
    })

    // 4. Returning to the tab re-reads the row — covers the case where the
    //    payment completed in another tab or the socket dropped.
    const onFocus = () => {
      if (!cancelled && document.visibilityState !== 'hidden') {
        void load({ silent: true })
      }
    }
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onFocus)

    // 5. Short polling window right after a payment, so the plan flips from
    //    FREE to active without the user reloading.
    const poll = window.setInterval(() => {
      if (cancelled) return
      if (isPaymentPending() || !hasLoadedRef.current) {
        void load({ silent: true })
      }
    }, PAYMENT_POLL_INTERVAL_MS)

    return () => {
      cancelled = true
      window.clearInterval(poll)
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onFocus)
      unsubscribe()
      supabase.removeChannel(channel)
    }
  }, [user?.id, load, applyProfile])

  /** Re-fetch after a scan event. Preserves last-known-good on failure. */
  const refetchCredits = useCallback(async () => {
    await load()
  }, [load])

  /** Manual refresh, and let every other mounted consumer refresh too. */
  const refresh = useCallback(async () => {
    await load({ attempts: 2 })
    notifyCreditsChanged()
  }, [load])

  /**
   * Verify with the backend before allowing a scan. On transient failure,
   * fall back to last-known-good state so valid users aren't blocked.
   */
  const verifyCanScan = useCallback(async (): Promise<{
    allowed: boolean
    reason?: string
  }> => {
    if (!user?.id) {
      return { allowed: false, reason: 'NOT_LOGGED_IN' }
    }

    try {
      const result = await fetchProfile()
      if (!result) {
        return { allowed: false, reason: 'FETCH_FAILED' }
      }
      applyProfile(result)
      if (result.isPro) return { allowed: true }
      if (result.scansCount > 0) return { allowed: true }
      return { allowed: false, reason: 'NO_CREDITS' }
    } catch (err) {
      console.error('[useScanCredits] verifyCanScan failed:', err)
      if (hasLoadedRef.current) {
        setRefreshError('Could not refresh credits')
        if (isProRef.current || scansCountRef.current > 0) {
          return { allowed: true }
        }
        return { allowed: false, reason: 'NO_CREDITS' }
      }
      setRefetchError('Failed to load credits')
      return { allowed: false, reason: 'VERIFICATION_FAILED' }
    }
  }, [user?.id, fetchProfile, applyProfile])

  const canScan = isPro || scansCount > 0

  return {
    isPro,
    scansCount,
    loading,
    refreshing,
    hasLoaded,
    canScan,
    refetchCredits,
    refresh,
    verifyCanScan,
    refetchError,
    refreshError,
  }
}
